import { Component, EventEmitter, Inject, Output, PLATFORM_ID } from '@angular/core';
import { SequenceService } from '../../../services/sequence.service';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-data-model',
  templateUrl: './data-model.component.html',
  styleUrls: ['./data-model.component.scss'],
})
export class DataModelComponent {
  // -------------------
  // App Info & Logs
  // -------------------
  currentUrl = '';
  userAgent = '';
  isOnline = false;
  localStorageCount = 0;
  sessionStorageCount = 0;
  logData: string | null = null;

  apiKey = '';
  apiMethod = 'GET';
  methods = ['GET', 'POST', 'PUT', 'DELETE'];
  collectionName = '';
  collections = [];
  filterQuery = '{}';
  updateData = '{}';
  limit = 1;
  sortBy = '';
  sortDescending = true;
  isLoading = false; 

  constructor(
    public sequence: SequenceService,
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.currentUrl = window.location.href;
      this.userAgent = navigator.userAgent;
      this.isOnline = navigator.onLine;
      this.localStorageCount = localStorage.length;
      this.sessionStorageCount = sessionStorage.length;

      window.addEventListener('online', () => (this.isOnline = true));
      window.addEventListener('offline', () => (this.isOnline = false));
    }
  }

  async initConsoleAndCollections() {
    this.isLoading = true;
    this.logToConsole('Initializing...');

    try {
      // const config = await this.sequence.getconfig(this.apiKey).toPromise();
      // this.apiKey = config?.GoogleSettings?.Key || '';
      const res: any = await this.sequence.sendRequest(this.apiKey, 'get').toPromise();
      if (res) {
        this.collections = res.sort((a: string, b: string) => a.localeCompare(b));;
        this.logToConsole('Collections loaded: \n' + this.collections.join('\n'));
        if (this.collections.length > 0) this.collectionName = this.collections[0];
      }
    } catch (err) {
      this.logToConsole('Error during initialization:\n' + JSON.stringify(err, null, 2));
    } finally {
      this.isLoading = false;
      this.logToConsole('Initialization complete.');
    }
  }

  close() {
    this.sequence._0xM2();
  }

  hardReload() {
    if (isPlatformBrowser(this.platformId)) {
      window.location.reload();
    }
  }

  async clearCache(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      localStorage.clear();
      sessionStorage.clear();

      // IndexedDB
      if ('indexedDB' in window) {
        const dbs = await (window as any).indexedDB.databases?.();
        if (dbs) {
          for (const db of dbs) {
            if (db.name) indexedDB.deleteDatabase(db.name);
          }
        }
      }
      // Service worker caches
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) {
      console.error(e);
    }
  }

  // -------------------
  // Show Storage in log
  // -------------------
  showStorage() {
    if (!isPlatformBrowser(this.platformId)) return;

    const parseValue = (value: string) => {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const local: Record<string, any> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)!;
      local[key] = parseValue(localStorage.getItem(key)!);
    }

    const session: Record<string, any> = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)!;
      session[key] = parseValue(sessionStorage.getItem(key)!);
    }

    this.logData = `LocalStorage:\n${JSON.stringify(local, null, 2)}\n\nSessionStorage:\n${JSON.stringify(session, null, 2)}`;
  }

  sendApiRequest() {
    if (!isPlatformBrowser(this.platformId)) return;

    if (!this.apiKey) {
      this.logToConsole('API key not loaded yet.');
      return;
    }

    if (!this.collectionName && this.apiMethod !== 'GET') {
      this.logToConsole('Please select a collection.');
      return;
    }

    let filterObj: any = {};
    let dataObj: any = {};

    
    try {
      filterObj = this.filterQuery ? JSON.parse(this.filterQuery) : {};
    } catch (err) {
      this.logToConsole('Invalid Filter JSON:\n' + err);
      return;
    }

    try {
      dataObj = this.updateData ? JSON.parse(this.updateData) : {};
    } catch (err) {
      this.logToConsole('Invalid Data JSON:\n' + err);
      return;
    }

    this.isLoading = true;

    this.logToConsole(`
----- REQUEST START -----
Action: ${this.apiMethod}
Collection: ${this.collectionName || 'N/A'}
Limit: ${this.limit}
SortBy: ${this.sortBy || 'N/A'}
SortDescending: ${this.sortDescending}
Filter: ${JSON.stringify(filterObj)}
Data: ${JSON.stringify(dataObj)}
-------------------------
  `);

    this.sequence
      .sendRequest(
        this.apiKey,
        this.apiMethod.toLowerCase() as 'get' | 'post' | 'put' | 'delete',
        this.collectionName,
        filterObj,
        dataObj,
        this.limit,
        this.sortBy,
        this.sortDescending,
      )
      .subscribe({
        next: (res) => {
          this.logToConsole('----- RESPONSE -----\n' + JSON.stringify(res, null, 2) + '\n--------------------');
          this.isLoading = false;
        },
        error: (err) => {
          this.logToConsole('----- ERROR -----\n' + JSON.stringify(err, null, 2) + '\n------------------');
          this.isLoading = false;
        },
      });
  }

  getfig() {
    this.logToConsole('Fetching configuration...');
    this.sequence.getconfig(this.apiKey).subscribe({
      next: (res) => {
        const data = JSON.stringify(res, null, 2);
        this.apiKey = res?.GoogleSettings?.Key;
        this.logToConsole(data);
        this.initConsoleAndCollections();
      },
      error: (err) => {
        this.logToConsole('Error fetching config:\n' + JSON.stringify(err, null, 2));
      },
    });
  }

  isConsoleOpen = false;
  consoleLogs = '';

  openConsole() {
    this.isConsoleOpen = true;
    // this.initConsoleAndCollections();
  }

  closeConsole() {
    this.isConsoleOpen = false;
  }

  logToConsole(msg: string) {
    this.consoleLogs += msg + '\n';
    setTimeout(() => {
      const el = document.querySelector('.console-body');
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }

  ClearlogToConsole() {
    this.consoleLogs = '';
  }
}
